import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { AuthShell, GoogleButton, GOOGLE_AUTH_ENABLED } from "@/components/auth/AuthShell";
import { PasswordFields } from "@/components/auth/PasswordFields";
import { useAuth } from "@/context/AuthContext";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", referral_source: "" });
  const [pwValid, setPwValid] = useState(false);
  const [tos, setTos] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwValid) {
      toast.error("Please meet all password requirements and confirm your password.");
      return;
    }
    if (!tos) {
      toast.error("Please accept the Terms of Service, Privacy Policy, and Returns Policy.");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, tos_accepted: tos, returns_accepted: tos, marketing_opt_in: marketing });
      toast.success("Account created! Let's finish setting up.");
      navigate("/welcome");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Join Startup Otter — it's free."
      footer={<>Already have an account? <Link to="/login" className="font-[560] text-primary hover:underline">Log in</Link></>}
    >
      {GOOGLE_AUTH_ENABLED && (
        <>
          <GoogleButton label="Sign up with Google" />
          <div className="my-5 flex items-center gap-3">
            <Separator className="flex-1" /><span className="text-xs text-muted-foreground">or</span><Separator className="flex-1" />
          </div>
        </>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="first">First name</Label>
            <Input id="first" required maxLength={100} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value.slice(0, 100) })} data-testid="register-firstname-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last">Last name</Label>
            <Input id="last" required maxLength={100} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value.slice(0, 100) })} data-testid="register-lastname-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" />
        </div>
        <PasswordFields
          password={form.password}
          setPassword={(v) => setForm((f) => ({ ...f, password: v }))}
          onValidChange={setPwValid}
          idPrefix="register"
        />
        <div className="space-y-1.5">
          <Label htmlFor="referral">
            Referred by / learned about us from <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input id="referral" maxLength={100} value={form.referral_source}
                 onChange={(e) => setForm({ ...form, referral_source: e.target.value.slice(0, 100) })}
                 placeholder="A friend's name, a podcast, an event…"
                 data-testid="register-referral-input" />
          <p className="text-xs text-muted-foreground">{form.referral_source.length}/100</p>
        </div>
        <div className="flex items-start gap-2.5">
          <Checkbox id="tos" checked={tos} onCheckedChange={(v) => setTos(!!v)} data-testid="auth-tos-checkbox" className="mt-0.5" />
          <Label htmlFor="tos" className="text-sm font-[440] leading-5 text-muted-foreground">
            I agree to the <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>, <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, and <Link to="/returns" className="text-primary hover:underline">Returns Policy</Link>.
          </Label>
        </div>
        <div className="flex items-start gap-2.5">
          <Checkbox id="marketing" checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} data-testid="register-marketing-checkbox" className="mt-0.5" />
          <Label htmlFor="marketing" className="text-sm font-[440] leading-5 text-muted-foreground">
            Keep me posted by email on new features, platform updates, community opportunities, and occasional promotions from Startup Otter.
            <span className="mt-1 block text-xs text-muted-foreground/80">You can unsubscribe at any time. See our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.</span>
          </Label>
        </div>
        <Button type="submit" className="w-full" disabled={loading || !pwValid || !tos} data-testid="register-submit-button">
          {loading ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
